import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import { AssetBalance, PostOnboardingCompletedResponseType, GetUserResponseType } from '@/entities';
import { telegramClientApi } from '@/shared';

export interface UserState {
  data: GetUserResponseType | null;
  loading: boolean;
  error: string | null;
}

const initialState: UserState = {
  data: null,
  loading: false,
  error: null
};

export const fetchUser = createAsyncThunk<
  GetUserResponseType,
  { withBalances?: boolean; captchaToken?: string },
  { rejectValue: string }
>('user/fetchUser', async (args, { rejectWithValue }) => {
  try {
    const captchaToken = args && typeof args === 'object' ? args.captchaToken : undefined;
    const res = await telegramClientApi.getUser(args.withBalances, captchaToken);

    if (res.error === null && res.data) {
      return res.data;
    } else if (res.error !== null) {
      return rejectWithValue(res.error.message);
    }

    return rejectWithValue('Unknown error occurred');
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const completeOnboarding = createAsyncThunk<
  PostOnboardingCompletedResponseType,
  void,
  { rejectValue: string }
>('user/completeOnboarding', async (_, { rejectWithValue }) => {
  try {
    const res = await telegramClientApi.completeOnboarding();

    if (res.httpCode === 200 && res.data) {
      return res.data;
    } else {
      return rejectWithValue(res.error?.message || 'Error reporting onboarding to API');
    }
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearUserData: (state) => {
      state.data = null;
      state.error = null;
    },
    resetUserState: () => initialState,
    updateUserBalances: (state, action: PayloadAction<AssetBalance[]>) => {
      if (state.data) {
        state.data.balance = action.payload;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch user';
      })
      .addCase(completeOnboarding.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(completeOnboarding.fulfilled, (state, action) => {
        state.loading = false;
        state.data = {
          user: action.payload.user,
          wallet: state.data?.wallet || null,
          balance: state.data?.balance || [],
        };
      })
      .addCase(completeOnboarding.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to complete onboarding';
      });
  }
});

export const { clearUserData, resetUserState, updateUserBalances } = userSlice.actions;

export default userSlice.reducer;
